import {
  buildMobileLocaleHref,
  getCurrentMobileRouteContext,
  isSystemLocale,
  type MobileLocationParts,
} from '@/config/tenant-route';
import {
  FULL_MOBILE_UI_LOCALES,
  fullMobileUiLocaleOptions,
  type I18nKey,
  localeOptions,
} from '@/i18n/locales';
import type { LocalLanguageType, LocalOption } from '@/i18n/types';
import {
  DEFAULT_SYSTEM_LOCALE,
  type SystemLocale,
  systemLocaleEnum,
} from '@ts-max/car-export-enum-sdk';
import antMobileArSA from 'antd-mobile/es/locales/ar-SA';
import antMobileDeDE from 'antd-mobile/es/locales/de-DE';
import antMobileEnUS from 'antd-mobile/es/locales/en-US';
import antMobileEsES from 'antd-mobile/es/locales/es-ES';
import antMobileFrFR from 'antd-mobile/es/locales/fr-FR';
import antMobileItIT from 'antd-mobile/es/locales/it-IT';
import antMobilePtBR from 'antd-mobile/es/locales/pt-BR';
import antMobileRuRU from 'antd-mobile/es/locales/ru-RU';
import antMobileZhCN from 'antd-mobile/es/locales/zh-CN';
import dayjs from 'dayjs';
import 'dayjs/locale/ar-sa';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/ru';
import 'dayjs/locale/zh-cn';

const localeEnvironmentMap = {
  [systemLocaleEnum.ZH_CN]: {
    antMobileLocale: antMobileZhCN,
    dayjsLocale: 'zh-cn',
  },
  [systemLocaleEnum.EN_US]: {
    antMobileLocale: antMobileEnUS,
    dayjsLocale: 'en',
  },
  [systemLocaleEnum.RU_RU]: {
    antMobileLocale: antMobileRuRU,
    dayjsLocale: 'ru',
  },
  [systemLocaleEnum.AR_SA]: {
    antMobileLocale: antMobileArSA,
    dayjsLocale: 'ar-sa',
  },
  [systemLocaleEnum.FR_FR]: {
    antMobileLocale: antMobileFrFR,
    dayjsLocale: 'fr',
  },
  [systemLocaleEnum.ES_ES]: {
    antMobileLocale: antMobileEsES,
    dayjsLocale: 'es',
  },
  [systemLocaleEnum.PT_BR]: {
    antMobileLocale: antMobilePtBR,
    dayjsLocale: 'pt-br',
  },
  [systemLocaleEnum.DE_DE]: {
    antMobileLocale: antMobileDeDE,
    dayjsLocale: 'de',
  },
  [systemLocaleEnum.IT_IT]: {
    antMobileLocale: antMobileItIT,
    dayjsLocale: 'it',
  },
} as const satisfies Record<
  SystemLocale,
  { antMobileLocale: typeof antMobileZhCN; dayjsLocale: string }
>;

/** Get the locale used by third-party Mobile components. */
export function getLocales(language: string) {
  const locale = normalizeLanguage(language);
  return localeEnvironmentMap[locale];
}

const FULL_MOBILE_UI_LOCALE_SET = new Set<string>(FULL_MOBILE_UI_LOCALES);

export function getFullMobileUiLanguage(language: string) {
  const locale = normalizeLanguage(language);
  return FULL_MOBILE_UI_LOCALE_SET.has(locale)
    ? (locale as (typeof FULL_MOBILE_UI_LOCALES)[number])
    : defaultLanguage;
}

export function getFullMobileUiLocales(language: string) {
  return localeEnvironmentMap[getFullMobileUiLanguage(language)];
}

export function isCarSourceShareRoute(routePath: string): boolean {
  return routePath === '/share' || routePath.startsWith('/share/');
}

export const defaultLanguage: LocalLanguageType = DEFAULT_SYSTEM_LOCALE;

/** Only exact supported canonical locale values are accepted. */
export function normalizeLanguage(lng: string): LocalLanguageType {
  return isSystemLocale(lng) ? lng : defaultLanguage;
}

export function applyLanguageEnvironment(
  language: LocalLanguageType,
  routePath = getCurrentMobileRouteContext().routePath,
): void {
  document.documentElement.lang = language;
  const interfaceLanguage = isCarSourceShareRoute(routePath)
    ? language
    : getFullMobileUiLanguage(language);
  dayjs.locale(localeEnvironmentMap[interfaceLanguage].dayjsLocale);
}

export function getLocaleOptions(): LocalOption[] {
  return fullMobileUiLocaleOptions;
}

export function getSystemLocaleOptions(): LocalOption[] {
  return localeOptions;
}

/** The URL route is the only source of truth for the current Mobile locale. */
export function getLanguage(): LocalLanguageType {
  return getCurrentMobileRouteContext().locale;
}

/** Full-page navigation intentionally refreshes Router basename, data and cache. */
export async function changeLanguage(
  lng: LocalLanguageType,
  location: MobileLocationParts & Pick<Location, 'replace'> = window.location,
) {
  const language = normalizeLanguage(lng);
  const targetHref = buildMobileLocaleHref(language, location);
  const currentHref = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;

  localStorage.setItem('i18nextLng', language);
  if (targetHref !== currentHref) location.replace(targetHref);
  return language;
}

export const isZhCN = () => getLanguage() === systemLocaleEnum.ZH_CN;

export const i18nKey = {
  t: (key: I18nKey) => key,
};
